/**
 * Build a `mailto:` URL pre-filled with a friendly reply body so the
 * super-admin Leads inbox can hand off to the team's mail client
 * without depending on an SMTP server. The Calendly URL is read from
 * `VITE_LEADS_CALENDLY_URL` and the body falls back gracefully when
 * the env var is unset (no broken "undefined" link in the email).
 *
 * Designed to be import-safe in tests/SSR: no `import.meta` access is
 * required for callers and the helper never throws on missing input.
 */

/**
 * Read the Calendly URL configured for the Leads inbox. Returns an
 * empty string if it is missing or blank so callers can decide
 * whether to render the booking line.
 */
export const getLeadsCalendlyUrl = () => {
  const fromEnv =
    typeof import.meta !== "undefined" && import.meta.env
      ? import.meta.env.VITE_LEADS_CALENDLY_URL
      : undefined;
  return typeof fromEnv === "string" ? fromEnv.trim() : "";
};

/**
 * Pick the best display name for the contact. Falls back to the
 * email local-part, then a generic salutation.
 */
export const formatContactGreeting = (lead) => {
  const name = (lead?.contactName || "").trim();
  if (name) {
    const first = name.split(/\s+/)[0];
    if (first) return first;
  }
  const email = (lead?.email || "").trim();
  if (email && email.includes("@")) {
    return email.split("@")[0];
  }
  return "there";
};

/**
 * Compose the email body. Includes a reference to the company name
 * (when present) and an optional Calendly booking line.
 */
export const buildLeadReplyBody = (lead, options = {}) => {
  const calendlyUrl =
    typeof options.calendlyUrl === "string"
      ? options.calendlyUrl.trim()
      : getLeadsCalendlyUrl();

  const greeting = `Hi ${formatContactGreeting(lead)},`;
  const company = (lead?.companyName || "").trim();
  const intro = company
    ? `Thanks for reaching out about Attunelogic for ${company}. We'd love to learn more about how your team is currently dispatching loads, tracking drivers, and handling paperwork so we can show you the parts of the platform that are most relevant to you.`
    : "Thanks for reaching out about Attunelogic. We'd love to learn more about how your team is currently dispatching loads, tracking drivers, and handling paperwork so we can show you the parts of the platform that are most relevant to you.";

  const bookingLine = calendlyUrl
    ? `If it's easier, you can grab a 30-minute slot directly on my calendar here: ${calendlyUrl}`
    : "Happy to grab a quick call this week or next - just reply with a couple of windows that work for you and I'll send an invite.";

  const closing = "Looking forward to connecting,\nThe Attunelogic Team";

  return [greeting, "", intro, "", bookingLine, "", closing].join("\n");
};

/**
 * Build the full `mailto:` URL with subject and body. Safe to call
 * with a missing email (returns an empty string so callers can
 * disable the button instead of opening a broken mail compose).
 */
export const buildLeadReplyMailto = (lead, options = {}) => {
  const email = (lead?.email || "").trim();
  if (!email) return "";

  const company = (lead?.companyName || "").trim();
  const subject = company
    ? `Following up on your Attunelogic demo request - ${company}`
    : "Following up on your Attunelogic demo request";
  const body = buildLeadReplyBody(lead, options);

  const params = new URLSearchParams({ subject, body });
  return `mailto:${email}?${params.toString()}`;
};

export default buildLeadReplyMailto;
